import React from "react";
import { Modal } from "newui/components/ui/Modal";
import dayjs from "newui/utils/dayjs";
import {
	AuditLogResource,
	formatAuditContextTypeLabel,
} from "newui/features/RolesPermissions/types/RolesPermissions.type";

export interface AuditEventDetailModalProps {
	event: AuditLogResource | null;
	isOpen: boolean;
	onClose: () => void;
}

const formatValue = (value: unknown): string => {
	if (value === null || value === undefined || value === "") {
		return "—";
	}

	if (typeof value === "object") {
		return JSON.stringify(value);
	}

	return String(value);
};

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
	<div className="flex items-start justify-between gap-4 border-b border-gray-100 py-2 last:border-b-0">
		<span className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</span>
		<span className="text-right text-sm text-gray-900">{value}</span>
	</div>
);

export const AuditEventDetailModal = ({ event, isOpen, onClose }: AuditEventDetailModalProps) => {
	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Audit Event Detail"
			iconType="info"
			size="lg"
			secondaryButton={{
				text: "Close",
				btntype: "secondary",
				onClick: onClose,
			}}
		>
			{event && (
				<div data-testid="audit-event-detail-modal-content">
					<DetailRow label="Action" value={event.action} />
					<DetailRow label="Category" value={event.action_category} />
					<DetailRow label="Actor" value={event.actor?.name ?? "System"} />
					<DetailRow label="Actor Type" value={formatValue(event.actor?.principal_type)} />
					<DetailRow label="Actor ID" value={formatValue(event.actor?.id)} />
					<DetailRow label="Context Type" value={formatAuditContextTypeLabel(event.context_type)} />
					<DetailRow label="Context ID" value={formatValue(event.context_id)} />
					<DetailRow label="IP Address" value={formatValue(event.ip_address)} />
					<DetailRow
						label="Timestamp"
						value={dayjs(event.created_at).format("DD MMM YYYY, h:mm:ss A")}
					/>

					<div className="mt-4">
						<h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">Changes</h4>
						{event.metadata?.changes?.length ? (
							<div
								data-testid="audit-event-detail-changes"
								className="divide-y divide-gray-100 rounded-md border border-gray-200"
							>
								{event.metadata.changes.map((change) => (
									<div key={change.field} className="grid grid-cols-3 gap-2 px-3 py-2 text-sm">
										<span className="font-medium text-gray-700">{change.field}</span>
										<span className="text-red-600 line-through">{formatValue(change.from)}</span>
										<span className="text-green-700">{formatValue(change.to)}</span>
									</div>
								))}
							</div>
						) : (
							<p className="text-sm text-gray-500" data-testid="audit-event-detail-no-changes">
								No field-level changes recorded for this event.
							</p>
						)}
					</div>
				</div>
			)}
		</Modal>
	);
};
